import { ReactNode } from 'react';
import styled from 'styled-components';
import useBackendPath from '../../hooks/useBackendPath';

import { colours, device } from '../../styles/constants';
import { lg, xl } from '../../styles/mixins';

export const Container = styled.div(() => {
  const { backendPath } = useBackendPath();

  return {
    alignItems: 'flex-start',
    background: `#666 url(${backendPath}/assets/images/artboards/artboard_3_mob.jpg) center top no-repeat`,
    backgroundSize: 'cover',
    bottom: 0,
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    left: 0,
    paddingBottom: 20,
    paddingTop: 110,
    right: 0,
    top: 0,
    transition: 'opacity 0.2s ease-in-out',
    [`@media ${device.tabletL}`]: {
      backgroundImage: `url(${backendPath}/assets/images/artboards/background-2.jpg)`,
      paddingBottom: lg(20),
    },
    [`@media ${device.desktop}`]: {
      paddingBottom: xl(20),
    },
    '&:before': {
      background: `rgba(255, 255, 255, 0.1)`,
      content: "''",
      height: '100%',
      left: 0,
      position: 'absolute',
      top: 0,
      width: '100%',
    },
  };
});

export const ContainerInner = styled.div({
  width: 'auto',
  [`@media ${device.tabletL}`]: {
    width: '60%',
  },
  [`@media ${device.laptop}`]: {
    width: '55%',
  },
});

export const Divider = styled.div({
  backgroundColor: colours.yellow,
  height: 2,
  marginTop: 23,
  width: 112,
  [`@media ${device.tabletL}`]: {
    height: lg(2),
    marginTop: lg(27),
    width: lg(112),
  },
  [`@media ${device.desktop}`]: {
    height: xl(2),
    marginTop: xl(27),
    width: xl(112),
  },
});

interface OptionsType {
  areTwoColumns?: boolean;
  children?: ReactNode;
}

export const Options = styled.ul<OptionsType>({
  backgroundColor: 'white',
  borderRadius: lg(4),
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  listStyle: 'none',
  overflow: 'hidden',
  margin: '25px 25px 0',
  padding: 0,
  [`@media ${device.tabletL}`]: {
    margin: `${lg(35)}px 0 0`,
  },
  [`@media ${device.desktop}`]: {
    borderRadius: xl(4),
    margin: `${xl(35)}px 0 0`,
  },
});

export const GridOptions = styled(Options)`
  display: grid;
  grid-template-columns: ${({ areTwoColumns }) =>
    areTwoColumns ? 'repeat(2, 1fr)' : 'repeat(1, 1fr)'};
  grid-template-rows: auto;
  grid-column-gap: 0;
  grid-row-gap: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  margin-bottom: 0;
`;

interface GridLastOptionsType extends OptionsType {
  numberOfItemsInLastRow: number;
}

export const GridLastOptions = styled(Options)<GridLastOptionsType>`
  display: grid;
  grid-template-rows: auto;
  grid-row-gap: 0;
  margin-top: 0;
  grid-template-columns: ${({ numberOfItemsInLastRow }) =>
    `repeat(${numberOfItemsInLastRow}, 1fr)`};
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: 1px solid rgb(171, 171, 171);
`;

export const Option = styled.li({
  position: 'relative',
  width: '100%',
  '&:before': {
    backgroundColor: '#ababab',
    content: "''",
    height: 1,
    left: 0,
    position: 'absolute',
    top: -1,
    width: '100%',
  },
  '&:after': {
    backgroundColor: '#ababab',
    content: "''",
    height: '100%',
    left: -1,
    position: 'absolute',
    top: 0,
    width: 1,
  },
});

export const OptionInput = styled.input({
  appearance: 'none',
  backgroundColor: 'white',
  margin: 0,
  opacity: 0,
  position: 'absolute',
  '&:checked + label': {
    backgroundColor: '#ccc',
  },
  '&:checked + label:before': {
    borderColor: '#999999',
  },
  '&:checked + label:after': {
    opacity: 1,
  },
});

interface OptionLabelProps {
  inputType?: string;
}

export const OptionLabel = styled.label<OptionLabelProps>(
  ({ inputType = 'checkbox' }) => ({
    alignItems: 'center',
    backgroundColor: 'white',
    color: 'black',
    cursor: 'pointer',
    display: 'flex',
    fontSize: 16,
    fontWeight: 400,
    padding: `10px 20px 10px 60px`,
    position: 'relative',
    transition: 'background-color 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: 'inherit',
    },
    [`@media ${device.laptop}`]: {
      fontSize: lg(18),
      padding: `15px ${lg(20)}px 15px ${lg(70)}px`,
      '&:hover': {
        backgroundColor: '#d7d7d7',
      },
    },
    [`@media ${device.desktop}`]: {
      fontSize: xl(18),
      padding: `15px ${xl(20)}px 15px ${xl(70)}px`,
    },
    '&:before': {
      border: `${lg(2)}px solid #999999`,
      borderRadius: inputType === 'checkbox' ? lg(4) : '50%',
      height: lg(24),
      content: "''",
      left: lg(21),
      marginTop: lg(-14),
      position: 'absolute',
      top: '50%',
      transition: 'borderColor 0.2s ease-in-out',
      width: lg(24),
      zIndex: 1,
      [`@media ${device.desktop}`]: {
        borderWidth: xl(2),
        borderRadius: inputType === 'checkbox' ? xl(4) : '50%',
        height: xl(24),
        left: xl(21),
        marginTop: xl(-14),
        width: xl(24),
      },
    },
    '&::after': {
      backgroundColor: inputType === 'checkbox' ? 'transparent' : 'black',
      borderBottom:
        inputType === 'checkbox' ? `${lg(3)}px solid black` : 'none',
      borderRight: inputType === 'checkbox' ? `${lg(3)}px solid black` : 'none',
      borderRadius: inputType === 'checkbox' ? 0 : '50%',
      content: "''",
      display: 'inline-block',
      height: inputType === 'checkbox' ? lg(14) : lg(10),
      left: inputType === 'checkbox' ? lg(30) : lg(29.5),
      marginTop: inputType === 'checkbox' ? lg(-11) : lg(-5.5),
      opacity: 0,
      position: 'absolute',
      top: '50%',
      transform: inputType === 'checkbox' ? 'rotate(40deg)' : 'none',
      transition: 'opacity 0.2s ease-in-out',
      width: inputType === 'checkbox' ? lg(6) : lg(10),
      zIndex: 0,
      [`@media ${device.desktop}`]: {
        borderBottomWidth: xl(3),
        borderRightWidth: xl(3),
        height: inputType === 'checkbox' ? xl(14) : xl(10),
        left: inputType === 'checkbox' ? xl(30) : xl(29.5),
        marginTop: inputType === 'checkbox' ? xl(-11) : xl(-5.5),
        width: inputType === 'checkbox' ? xl(6) : xl(10),
      },
    },
  }),
);

export const Category = styled.h4({
  color: colours.yellow,
  fontSize: 13,
  fontWeight: 800,
  fontStretch: 'condensed',
  lineHeight: 1,
  margin: 0,
  textTransform: 'uppercase',
  [`@media ${device.tabletL}`]: {
    fontSize: lg(23.2),
  },
  [`@media ${device.desktop}`]: {
    fontSize: xl(23.2),
  },
});

export const QuestionNumber = styled.h3({
  color: 'white',
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 1,
  margin: '0 4px 0 0',
  display: 'flex',
  alignItems: 'center',
  [`@media ${device.tabletL}`]: {
    fontSize: lg(17),
  },
  [`@media ${device.desktop}`]: {
    fontSize: xl(20),
  },
});

export const QuestionStep = styled.div({
  marginTop: 0,
  [`@media ${device.tabletP}`]: {
    display: 'flex',
    alignItems: 'center',
  },
  [`@media ${device.tabletL}`]: {
    fontSize: lg(16),
  },
  [`@media ${device.desktop}`]: {
    fontSize: xl(18),
  },
});

export const QuestionHelperText = styled.span({
  color: 'white',
  display: 'inline-block',
  fontSize: 17,
  fontStyle: 'italic',
  fontWeight: 400,
  opacity: 0.8,
  [`@media ${device.tabletL}`]: {
    fontSize: lg(17),
  },
  [`@media ${device.desktop}`]: {
    fontSize: xl(18),
  },
});

export const Heading = styled.h1({
  color: 'white',
  fontSize: 20,
  fontWeight: 400,
  lineHeight: 1.15,
  margin: '0 80px 0 0',
  [`@media ${device.tabletL}`]: {
    fontSize: lg(28),
    marginRight: 0,
  },
  [`@media ${device.desktop}`]: {
    fontSize: xl(28),
  },
});

interface QuestionButtonType {
  primary?: boolean;
}

export const QuestionButton = styled.button<QuestionButtonType>(
  ({ primary = false }) => ({
    alignItems: 'center',
    backgroundColor: primary ? colours.red : 'transparent',
    border: primary ? 'none' : `1px solid ${colours.white} !important`,
    borderRadius: lg(5),
    color: colours.white,
    cursor: 'pointer',
    display: 'flex',
    fontSize: lg(13),
    fontWeight: 700,
    gap: 10,
    lineHeight: 1,
    height: lg(50),
    justifyContent: 'space-evenly',
    marginLeft: lg(8),
    padding: '0 25px',
    position: 'relative',
    transition:
      'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out',
    [`@media ${device.tabletL}`]: {
      height: lg(62),
      fontSize: lg(18),
      gap: lg(15),
      marginLeft: lg(14),
      padding: `0 ${lg(33)}px`,
    },
    [`@media ${device.desktop}`]: {
      borderRadius: xl(5),
      fontSize: xl(18),
      gap: xl(15),
      height: xl(62),
      marginLeft: xl(14),
      padding: `0 ${xl(33)}px`,
    },
    '&:hover': {
      backgroundColor: primary ? colours.activeRed : 'transparent',
      opacity: primary ? 1 : 0.8,
    },
    '&:disabled': {
      cursor: 'default',
      pointerEvents: 'none',
      opacity: 0.6,
    },
    img: {
      width: primary ? lg(7) : lg(16),
      [`@media ${device.tabletL}`]: {
        width: primary ? lg(11) : lg(20),
      },
      [`@media ${device.desktop}`]: {
        width: primary ? xl(11) : xl(20),
      },
    },
  }),
);

export const QuestionHeader = styled.div({
  alignItems: 'flex-start',
  display: 'flex',
  position: 'relative',
  img: {
    position: 'absolute',
    right: 20,
    width: 80,
    [`@media ${device.tabletL}`]: {
      position: 'relative',
      right: 0,
    },
  },
});

export const QuestionHeaderContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  paddingLeft: 25,
  paddingRight: 25,
  width: '100%',
  [`@media ${device.tabletL}`]: {
    gap: lg(10),
    paddingLeft: lg(40),
    paddingRight: 0,
  },
  [`@media ${device.desktop}`]: {
    gap: xl(10),
    paddingLeft: xl(40),
  },
});

export const ButtonContainer = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  margin: '11px 25px 0',
  [`@media ${device.tabletL}`]: {
    margin: `${lg(16)}px 0 0`,
  },
  [`@media ${device.desktop}`]: {
    margin: `${xl(16)}px 0 0`,
  },
});

export const LeftArrow = styled.img({
  transform: 'rotate(180deg)',
});
